﻿import React, { useEffect, useState } from 'react';
import { ModalBody, ModalHeader } from 'reactstrap';
import { faCheck, faCircleInfo, faCopy } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Rodal from '../../Rodal/Rodal';
import ButtonLoader from '../../Layout/Buttons/ButtonLoader';
import { WireInstructions } from '../../../models/FlowOfFunds/flow-of-funds.model';
import { EM_DASH } from '../../../utils/flowOfFundsFormat';
import {
    ROUTING_NUMBER_ERROR,
    SWIFT_CODE_ERROR,
    isRoutingNumberValid,
    isSwiftCodeValid,
} from '../../../utils/wireInstructionsValidation';
import './WireInstructionsModal.scss';

type FieldKey = keyof WireInstructions;

const SECTIONS: { title: string; fields: [string, FieldKey][] }[] = [
    {
        title: 'Recipient (Beneficiary)',
        fields: [
            ['Recipient Name', 'beneficiaryName'],
            ['Recipient Address', 'beneficiaryAddress'],
        ],
    },
    {
        title: 'Receiving Bank',
        fields: [
            ['Name of Bank', 'bankName'],
            ['Bank Address', 'bankAddress'],
            ['Routing Number (ABA)', 'routingNumber'],
            ['SWIFT / BIC', 'swiftCode'],
        ],
    },
    {
        title: 'Beneficiary Account',
        fields: [
            ['Account Name', 'accountName'],
            ['Account Number', 'accountNumber'],
            ['Account Type', 'accountType'],
        ],
    },
    {
        title: 'Other Instructions',
        fields: [
            ['For Further Credit (FFC)', 'forFurtherCredit'],
            ['Wire Reference / Memo', 'referenceNote'],
        ],
    },
];

const ALL_FIELDS: FieldKey[] = [
    'recipientName',
    'purpose',
    ...SECTIONS.flatMap((section) => section.fields.map(([, key]) => key)),
];

const FIELD_VALIDATORS: Partial<Record<FieldKey, (value?: string) => string | null>> = {
    routingNumber: (value) => (isRoutingNumberValid(value) ? null : ROUTING_NUMBER_ERROR),
    swiftCode: (value) => (isSwiftCodeValid(value) ? null : SWIFT_CODE_ERROR),
};

const VALIDATED_FIELDS = Object.keys(FIELD_VALIDATORS) as FieldKey[];

const findErrors = (instructions: WireInstructions) =>
    VALIDATED_FIELDS.reduce<Partial<Record<FieldKey, string>>>((errors, key) => {
        const message = FIELD_VALIDATORS[key]?.(instructions[key]);
        return message ? { ...errors, [key]: message } : errors;
    }, {});

const isSameInstructions = (left: WireInstructions, right: WireInstructions) =>
    ALL_FIELDS.every((key) => (left[key] ?? '').trim() === (right[key] ?? '').trim());

const CopyButton = ({ getText }: { getText: () => string }) => {
    const [hasCopied, setHasCopied] = useState(false);

    useEffect(() => {
        if (!hasCopied) return undefined;
        const timer = setTimeout(() => setHasCopied(false), 1200);
        return () => clearTimeout(timer);
    }, [hasCopied]);

    const copy = () => {
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(getText()).catch(() => {});
        }
        setHasCopied(true);
    };

    return (
        <button
            type="button"
            aria-label="Copy"
            className={`ff-wire__copy${hasCopied ? ' is-copied' : ''}`}
            onClick={copy}>
            <FontAwesomeIcon icon={hasCopied ? faCheck : faCopy} />
        </button>
    );
};

interface WireInstructionsModalProps {
    isOpen: boolean;
    instructions: WireInstructions | null;
    onClose: () => void;
    onSave?: (instructions: WireInstructions) => Promise<boolean>;
    isReadOnly?: boolean;
    startInEditMode?: boolean;
}

export const WireInstructionsModal = ({
    isOpen,
    instructions,
    onClose,
    onSave,
    isReadOnly = false,
    startInEditMode = false,
}: WireInstructionsModalProps) => {
    const canEdit = !isReadOnly && !!onSave;

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [draft, setDraft] = useState<WireInstructions>(instructions ?? {});
    const [touched, setTouched] = useState<Partial<Record<FieldKey, boolean>>>({});

    const errors = findErrors(draft);

    useEffect(() => {
        if (!isEditing) {
            setDraft(instructions ?? {});
        }
    }, [instructions, isEditing]);

    useEffect(() => {
        setIsEditing(isOpen && startInEditMode && canEdit);
        setIsSaving(false);
        setTouched({});
    }, [isOpen, startInEditMode, canEdit]);

    const isEditingNow = isEditing && canEdit;

    const setField = (key: FieldKey, value: string) =>
        setDraft((current) => ({ ...current, [key]: value }));

    const saveDetails = async () => {
        if (Object.keys(errors).length > 0) {
            setTouched(
                VALIDATED_FIELDS.reduce((all, key) => ({ ...all, [key]: true }), {})
            );
            return;
        }
        if (isSameInstructions(draft, instructions ?? {})) {
            setIsEditing(false);
            return;
        }
        setIsSaving(true);
        try {
            const succeeded = await onSave?.(draft);
            if (succeeded) {
                setIsEditing(false);
            }
        } finally {
            setIsSaving(false);
        }
    };

    const copyAll = () => {
        const lines = SECTIONS.flatMap((section) =>
            section.fields.map(([label, key]) => `${label}: ${draft[key] || EM_DASH}`)
        );
        const recipientLine = draft.recipientName
            ? [`Recipient Name: ${draft.recipientName}`]
            : [];
        const text = [...recipientLine, ...lines].join('\n');
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(text).catch(() => {});
        }
    };

    return (
        <Rodal
            visible={isOpen}
            onClose={onClose}
            animation="fade"
            showMask={true}
            width={560}
            showCloseButton={true}
            className="ff-wire-modal">
            <ModalHeader>Wire Instructions</ModalHeader>

            <ModalBody>
                <div className="ff-wire__notice">
                    <FontAwesomeIcon icon={faCircleInfo} className="ff-wire__notice-icon" />
                    <span>
                        Always confirm these details by phone with a known contact before
                        sending funds. VetValue does not change wire details by email.
                    </span>
                </div>

                {SECTIONS.map((section) => (
                    <div key={section.title} className="ff-wire__section">
                        <p className="ff-wire__section-title">{section.title}</p>
                        {section.fields.map(([label, key]) => {
                            const error = touched[key] ? errors[key] : undefined;
                            return (
                                <div key={key} className="ff-wire__row">
                                    <span className="ff-wire__label">{label}</span>
                                    <span className="ff-wire__value-wrap">
                                        {isEditingNow ? (
                                            <span className="ff-wire__field">
                                                <input
                                                    type="text"
                                                    className={`form-control ff-wire__input${
                                                        error ? ' is-invalid' : ''
                                                    }`}
                                                    value={draft[key] ?? ''}
                                                    onChange={(e) => setField(key, e.target.value)}
                                                    onBlur={() =>
                                                        setTouched((current) => ({
                                                            ...current,
                                                            [key]: true,
                                                        }))
                                                    }
                                                />
                                                {error && (
                                                    <span className="ff-wire__error">{error}</span>
                                                )}
                                            </span>
                                        ) : (
                                            <span className="ff-wire__value">
                                                {draft[key] || EM_DASH}
                                            </span>
                                        )}
                                        {!isEditingNow && (
                                            <CopyButton getText={() => draft[key] || ''} />
                                        )}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                ))}

                <div className="buttons-wrapper">
                    <button type="button" className="btn btn-secondary" onClick={onClose}>
                        Close
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={copyAll}>
                        Copy All Details
                    </button>
                    {canEdit &&
                        (isEditingNow ? (
                            <ButtonLoader
                                buttonText="Save Details"
                                loaderButtonText="Saving..."
                                isLoading={isSaving}
                                disabled={isSaving}
                                className="btn btn-primary"
                                onClick={saveDetails}
                            />
                        ) : (
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => setIsEditing(true)}>
                                Edit Details
                            </button>
                        ))}
                </div>
            </ModalBody>
        </Rodal>
    );
};
